import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchOrders, fetchOrderById, verifyOrder } from '../store/slices/orderManagementSlice';
import { OrderVerificationDiscrepancy } from '../types/order';
import { 
  CheckCircle, 
  AlertTriangle, 
  Package, 
  User, 
  Clock, 
  Plus,
  Trash2,
  Eye,
  RefreshCw
} from 'lucide-react';

const OrderVerification: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { orders, loading, error, selectedOrder } = useSelector((state: RootState) => state.orderManagement);
  
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [discrepancies, setDiscrepancies] = useState<OrderVerificationDiscrepancy[]>([]);
  const [verifying, setVerifying] = useState(false);

  // Filter orders that are ready for verification (completed status)
  const completedOrders = orders.filter(order => order.status === 'completed');

  useEffect(() => {
    // Fetch completed orders for verification
    dispatch(fetchOrders({ status: 'completed' }));
  }, [dispatch]);

  const handleSelectOrder = async (orderId: number) => {
    setSelectedOrderId(orderId);
    try {
      await dispatch(fetchOrderById(orderId)).unwrap();
      // Reset verification form
      setVerificationNotes('');
      setDiscrepancies([]);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    }
  };

  const handleAddDiscrepancy = () => {
    if (!selectedOrder) return;
    
    const newDiscrepancy: OrderVerificationDiscrepancy = {
      item_id: selectedOrder.items[0]?.id || 0,
      issue: '',
      resolution: ''
    };
    setDiscrepancies([...discrepancies, newDiscrepancy]);
  };

  const handleUpdateDiscrepancy = (index: number, field: keyof OrderVerificationDiscrepancy, value: string | number) => {
    const updated = [...discrepancies];
    updated[index] = { ...updated[index], [field]: value };
    setDiscrepancies(updated);
  };

  const handleRemoveDiscrepancy = (index: number) => {
    setDiscrepancies(discrepancies.filter((_, i) => i !== index));
  };

  const handleVerifyOrder = async () => {
    if (!selectedOrder) return;

    setVerifying(true);
    try {
      await dispatch(verifyOrder({
        orderId: selectedOrder.id,
        verificationData: {
          verification_notes: verificationNotes,
          discrepancies: discrepancies
        }
      })).unwrap();

      // Reset form and refresh orders
      setVerificationNotes('');
      setDiscrepancies([]);
      setSelectedOrderId(null);
      dispatch(fetchOrders({ status: 'completed' }));
      
      alert('Order verified successfully!');
    } catch (error: any) {
      console.error('Verification failed:', error);
      alert(`Verification failed: ${error}`);
    } finally {
      setVerifying(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchOrders({ status: 'completed' }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Order Verification</h1>
          <p className="mt-2 text-gray-600">Verify and approve completed orders (Executive 2 only)</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Completed Orders ({completedOrders.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {completedOrders.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2">No completed orders to verify</p>
              </div>
            ) : (
              completedOrders.map((order) => (
                <div
                  key={order.id}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedOrderId === order.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => handleSelectOrder(order.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {order.order_number}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.total_items} items • {order.order_date}
                      </p>
                      {order.assigned_worker && (
                        <div className="flex items-center mt-2 text-xs text-gray-600">
                          <User className="h-3 w-3 mr-1" />
                          {order.assigned_worker.full_name || order.assigned_worker.username}
                        </div>
                      )}
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Completed
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Order Details & Verification */}
        <div className="bg-white shadow rounded-lg">
          {selectedOrder ? (
            <div className="h-full flex flex-col">
              {/* Order Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">
                      {selectedOrder.order_number}
                    </h2>
                    <p className="text-sm text-gray-500">{selectedOrder.total_items} items</p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {selectedOrder.completed_at && new Date(selectedOrder.completed_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="px-6 py-4 border-b border-gray-200 flex-1 overflow-y-auto">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Order Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.style_name}</p>
                        <p className="text-xs text-gray-600">
                          {item.sku} • {item.size} • {item.color}
                        </p>
                        {item.location && (
                          <p className="text-xs text-blue-600 mt-1">Location: {item.location}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">Qty: {item.quantity}</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'picked' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verification Form */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Verification</h3>
                
                {/* Verification Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Notes
                  </label>
                  <textarea
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add any verification notes or comments..."
                  />
                </div>

                {/* Discrepancies */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Discrepancies
                    </label>
                    <button
                      onClick={handleAddDiscrepancy}
                      className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add</span>
                    </button>
                  </div>
                  
                  {discrepancies.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No discrepancies reported</p>
                  ) : (
                    <div className="space-y-3">
                      {discrepancies.map((discrepancy, index) => (
                        <div key={index} className="border border-gray-200 rounded-md p-3">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Discrepancy {index + 1}</h4>
                            <button
                              onClick={() => handleRemoveDiscrepancy(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Item</label>
                              <select
                                value={discrepancy.item_id}
                                onChange={(e) => handleUpdateDiscrepancy(index, 'item_id', parseInt(e.target.value))}
                                className="w-full border border-gray-300 rounded text-xs px-2 py-1"
                              >
                                {selectedOrder.items.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.sku} - {item.style_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Issue</label>
                              <input
                                type="text"
                                value={discrepancy.issue}
                                onChange={(e) => handleUpdateDiscrepancy(index, 'issue', e.target.value)}
                                className="w-full border border-gray-300 rounded text-xs px-2 py-1"
                                placeholder="Describe the issue..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Resolution</label>
                              <input
                                type="text"
                                value={discrepancy.resolution}
                                onChange={(e) => handleUpdateDiscrepancy(index, 'resolution', e.target.value)}
                                className="w-full border border-gray-300 rounded text-xs px-2 py-1"
                                placeholder="How was it resolved..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-6 py-4">
                <div className="flex space-x-3">
                  <button
                    onClick={handleVerifyOrder}
                    disabled={verifying}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>{verifying ? 'Verifying...' : 'Verify Order'}</span>
                  </button>
                  <button
                    onClick={() => setSelectedOrderId(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center text-gray-500">
                <Eye className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2">Select an order to verify</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderVerification;